import React, { useEffect, useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Austrian date input. Accepts/emits ISO (YYYY-MM-DD) for API compatibility,
 * but displays + accepts user input as DD.MM.YYYY (DACH locale).
 *
 * Props:
 *   value:    string in "YYYY-MM-DD" (or "" / null)
 *   onChange: (iso: string) => void   — "" when invalid/empty
 *   testId, placeholder, disabled, className
 */
function isoToAT(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function atToIso(at) {
  if (!at) return "";
  const m = at.trim().match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (!m) return "";
  let [, d, mo, y] = m;
  if (y.length === 2) y = (Number(y) > 50 ? "19" : "20") + y;
  const dd = d.padStart(2, "0");
  const mm = mo.padStart(2, "0");
  const dt = new Date(`${y}-${mm}-${dd}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return "";
  if (dt.getFullYear() !== Number(y) || dt.getMonth() + 1 !== Number(mm) || dt.getDate() !== Number(dd)) return "";
  return `${y}-${mm}-${dd}`;
}

// Mask DD.MM.YYYY while typing (auto-insert dots, max 10 chars)
function maskAT(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let out = digits;
  if (digits.length > 4) out = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
  else if (digits.length > 2) out = `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return out;
}

export default function DateInputAT({
  value,
  onChange,
  placeholder = "TT.MM.JJJJ",
  disabled,
  className = "",
  testId,
  ...rest
}) {
  const [text, setText] = useState(() => isoToAT(value));

  // Sync from external value changes
  useEffect(() => {
    const next = isoToAT(value);
    if (next !== text && document.activeElement !== inputRef.current) setText(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const inputRef = React.useRef(null);

  const handleChange = (e) => {
    const masked = maskAT(e.target.value);
    setText(masked);
    if (masked === "") {
      onChange?.("");
      return;
    }
    const iso = atToIso(masked);
    if (iso) onChange?.(iso);
  };

  const handleBlur = () => {
    if (text === "") return;
    const iso = atToIso(text);
    if (iso) setText(isoToAT(iso));
    else {
      // Invalid → revert to last good value
      setText(isoToAT(value));
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={10}
        className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 pr-9 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand/50 focus:ring-1 focus:ring-brand/30 disabled:opacity-50 num tabular-nums"
        data-testid={testId}
        {...rest}
      />
      <Calendar
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
      />
    </div>
  );
}
